<script>
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { getGroupStore } from '@stores/group';
  let recsStore = getGroupStore('recsStore');
  import { getTextElementNumberOfLines } from '@utils/browser-utils';
  import PreloadBox from '@components/PreloadBox.svelte';

  import Review from '@app-components/rec_object/parts/Review.svelte';

  import jQuery from 'jquery';

  export let withFooter = false;
  export let recObjectsWithLocation = [];
  export let recObjectId;
  export let mobile;
  export let positionStyle;

  let detailCardEl;

  $: if (positionStyle && detailCardEl) {
    const card = jQuery(detailCardEl);

    const newStyle = Object.keys(positionStyle)
      .map((styleKey) => {
        return `${styleKey}: ${positionStyle[styleKey]}`;
      })
      .join('; ');

    card.attr('style', [card.attr('style'), newStyle].join('; '));
  }

  let recs = [];
  let reviewLines = 2;

  const unsubRecs = recsStore.subscribe(async (newRecs) => {
    recs = newRecs;
  });

  const dispatch = createEventDispatcher();
  const dispatchClick = () => {
    dispatch('click');
  };

  let photoData = {};
  $: recObject = recObjectId ? recObjectsWithLocation.find((recObject) => recObject.id === recObjectId) : undefined;

  $: rec = recObject ? recs.find((rec) => rec.rec_object_id === recObject.id) : undefined;

  $: {
    if (!recObject) {
      photoData = {};
    } else {
      let sortOrder = recObject.photos_sort_order.map((x) => {
        return parseInt(x);
      });

      const photos = recObject.rec_object_photos.sort(function (a, b) {
        return sortOrder.indexOf(a.id) - sortOrder.indexOf(b.id);
      });

      const photo = photos[0];

      photoData = {
        displayStyle: photo.display_style,
        url: photo.view_urls.rect_size || photo.view_urls.full_size,
        src: photo.view_urls.full_size,
      };
    }
  }

  let adjustReviewLinesTimeout = undefined;
  const adjustReviewLines = () => {
    if (adjustReviewLinesTimeout) {
      return;
    }

    adjustReviewLinesTimeout = setTimeout(() => {
      adjustReviewLinesTimeout = undefined;

      const titleLines = getTextElementNumberOfLines(`#detail-card-for-${recObjectId} .detail-card__title`);

      reviewLines = titleLines >= 2 ? 2 : 3;
    }, 0);
  };

  $: if (recObjectId) {
    adjustReviewLines();
  }

  let imageIsLoaded = false;
  const onImageLoad = () => {
    imageIsLoaded = true;
  };

  $: if (recObjectId) {
    imageIsLoaded = false;
  }

  onDestroy(() => {
    unsubRecs();
  });
</script>

<style style="text/scss" lang="scss">
  @import '../../vendor/design-system.scss';

  $detail-card-margin-x: 35px;
  $detail-card-margin-y: 25px;
  $detail-card-height: 104px;
  $detail-card-border-radius: 12px;
  $mapbox-attr-height: 20px;

  $detail-card-image-height: 88px;
  $detail-card-image-width: 111px;
  $detail-card-image-border-radius: 8px;

  .detail-card {
    z-index: 3;
    position: fixed;
    margin: $detail-card-margin-y $detail-card-margin-x;
    margin-bottom: $detail-card-margin-y + $mapbox-attr-height;
    height: $detail-card-height;
    border-radius: $detail-card-border-radius;
    background: white;
    box-shadow: 0px 13px 40px rgb(0 0 0 / 18%);
    padding: calc(($detail-card-height - $detail-card-image-height) / 2);

    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .detail-card__image {
      height: $detail-card-image-height;
      width: $detail-card-image-width;
      flex-shrink: 0;

      img {
        height: $detail-card-image-height;
        width: $detail-card-image-width;
        object-fit: cover;
        border-radius: $detail-card-image-border-radius;

        &.contain {
          background-color: white;
          object-fit: contain;
          border: 1px solid $neutral-input-gray;
        }
      }

      .detail-card__image-placeholder {
        height: 100%;
      }

      .hidden {
        display: none;
      }
    }

    .detail-card__content {
      height: 100%;
      flex-grow: 1;
      padding-left: 8px;
      padding-right: 2px;
      align-self: flex-start;
      display: block;
      text-align: left;

      .detail-card__title {
        font-size: 16px;
        font-weight: 500;
        text-align: left;
        -webkit-line-clamp: 2;
        word-break: break-word;
        overflow: hidden;
        display: -webkit-inline-box;
        -webkit-box-orient: vertical;
        margin-bottom: 0px;
        min-height: 26px;
      }

      .detail-card__review {
        width: 100%;
        min-height: 36px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;

        :global(p) {
          margin: 0px;
        }
      }
    }

    &.mobile {
      $screen-width-minus-margin: calc(100% - $detail-card-margin-x * 2);
      $detail-card-max-width-mobile: 480px;
      $detail-card-actual-width: min($detail-card-max-width-mobile, $screen-width-minus-margin);

      width: $screen-width-minus-margin;
      max-width: $detail-card-max-width-mobile;
      bottom: 24px;
      left: calc(($screen-width-minus-margin - $detail-card-actual-width) / 2);
      z-index: 22;

      &.with-footer {
        $footer-height: 88px;
        bottom: $footer-height;
      }
    }

    &.desktop {
      width: 340px;
      margin: 0px;
      margin-bottom: 0px;
    }
  }
</style>

{#if rec}
  <div
    class="detail-card"
    class:with-footer={withFooter}
    class:mobile={mobile}
    class:desktop={!mobile}
    bind:this={detailCardEl}
    on:click={dispatchClick}
    id={`detail-card-for-${recObjectId}`}>
    <div class="detail-card__image">
      <img
        class={photoData.displayStyle}
        href={photoData.href}
        src={photoData.src}
        alt="rec-card"
        rel="${`gallery-${recObjectId}`}"
        class:hidden={!imageIsLoaded}
        on:load={() => onImageLoad()} />

      <div class="detail-card__image-placeholder" class:hidden={imageIsLoaded}>
        <PreloadBox />
      </div>
    </div>
    <div class="detail-card__content">
      <div class="detail-card__title">
        {recObject.display_name}
      </div>

      <div class="detail-card__review">
        {#if rec && rec.user_note}
          <Review
            style="flex-direction: row; align-items: stretch !important; width: 100%;"
            nameDescriptionStyle="margin-left: 0px; display: flex; flex-direction: row; align-items: center;"
            descriptionStyle="line-height: 16px; text-align: left; font-size: 12px; margin-top: 0px !important;"
            avatarStyle="margin: auto; flex-basis: 30px;"
            mobile={true}
            showAvatar={true}
            avatarSizePx={23}
            showNames={false}
            showUpDownArrow={false}
            shaved={false}
            shavedOnFirstLoad={false}
            i={0}
            editable={false}
            expandable={false}
            id={rec && rec.id}
            lines={reviewLines} />
        {/if}
      </div>
    </div>
  </div>
{/if}
